'use client';

import { useEffect, useState } from 'react';
import { Area } from 'react-easy-crop';

import getCroppedImg from './CollectionThumbnailModal.utils';

import { PATH } from '@/shared/constants';

const DEFAULT_ZOOM = 1;
const DEFAULT_CROP = { x: 0, y: 0 };
const DEFAULT_THUMBNAIL_IMAGE_URL = `${PATH.MEDIA_YOZM_IMAGE}/default_collection_thumbnail.svg`;

enum ZoomType {
  IN = 'in',
  OUT = 'out',
}

type ThumbnailImage = {
  imageUrl: string | null;
  imageFile: File | null;
  imageSrc: string;
  imageType?: 'file' | 'url' | 'none' | null;
};

interface UseThumbnailCreateProps {
  image: string | null;
}
const getPathWithoutExtension = (url: string) => {
  const withoutDomain = url.split('/').slice(3).join('');
  return withoutDomain.split('.')[0];
};

const useThumbnail = ({
  image = DEFAULT_THUMBNAIL_IMAGE_URL,
}: UseThumbnailCreateProps) => {
  const [thumbnailImage, setThumbnailImage] = useState<ThumbnailImage>({
    imageUrl: null,
    imageFile: null,
    imageType: null,
    imageSrc: DEFAULT_THUMBNAIL_IMAGE_URL,
  });
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
  const [crop, setCrop] = useState<{ x: number; y: number }>(DEFAULT_CROP);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const handleZoomChange = (type: ZoomType) => {
    if (thumbnailImage.imageSrc === DEFAULT_THUMBNAIL_IMAGE_URL) return;

    setZoom((prev) => {
      if (type === ZoomType.IN) {
        return Math.min(prev * 10 + 1, 20) / 10;
      } else {
        return Math.max(prev * 10 - 1, 10) / 10;
      }
    });
  };

  const initImage = () => {
    setZoom(DEFAULT_ZOOM);
    setCrop(DEFAULT_CROP);
    setCroppedAreaPixels(null);
    setThumbnailImage({
      imageSrc: DEFAULT_THUMBNAIL_IMAGE_URL,
      imageUrl: null,
      imageFile: null,
      imageType: null,
    });
  };

  const handleCropComplete = (croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getCroppedFile = async () => {
    if (croppedAreaPixels && thumbnailImage.imageSrc) {
      try {
        const isExternalAiImage = !thumbnailImage.imageFile;
        const randomAIImageName = getPathWithoutExtension(
          thumbnailImage.imageSrc
        ).slice(-5);

        return await getCroppedImg({
          isExternalAiImage: isExternalAiImage,
          pixelCrop: croppedAreaPixels,
          imageSrc: thumbnailImage.imageSrc,
          fileName: isExternalAiImage
            ? randomAIImageName
            : thumbnailImage.imageFile?.name,
        });
      } catch (e) {
        alert('잠시 후 다시 시도해주세요.');
      }
    }
  };

  const handleSliderChange = (value: number) => {
    setZoom(value);
  };

  useEffect(() => {
    setThumbnailImage({
      imageSrc: image || DEFAULT_THUMBNAIL_IMAGE_URL,
      imageUrl: null,
      imageFile: null,
      imageType: null,
    });
  }, [image]);

  return {
    value: {
      thumbnail: {
        zoom,
        crop,
        imageSrc: thumbnailImage.imageSrc,
        imageType: thumbnailImage.imageType,
        imageUrl: thumbnailImage.imageUrl,
        imageFile: thumbnailImage.imageFile,
      },
      slider: {
        min: DEFAULT_ZOOM,
        max: zoom,
      },
    },
    onChange: {
      thumbnail: {
        zoom: setZoom,
        crop: setCrop,
        cropComplete: handleCropComplete,
        setImage: setThumbnailImage,
      },
      slider: {
        set: handleSliderChange,
        minus: () => handleZoomChange(ZoomType.OUT),
        plus: () => handleZoomChange(ZoomType.IN),
      },
    },
    init: initImage,
    getCroppedFile,
  };
};

export default useThumbnail;
